import React, { useEffect, useRef, useState } from "react";
import {
  createInquiry,
  getAboutCarouselSlides,
  getCampusActivities,
  getShiningStars,
  getStudentsByDob,
  getTeachers
} from "../services/api";
import "./PublicSite.css";

const ABOUT_CAROUSEL_VISIBLE_COUNT = 5;

function SocialIcon({ type }) {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  };

  const icons = {
    facebook: (
      <svg {...commonProps}>
        <path d="M15 8h-2a2 2 0 0 0-2 2v2h4l-.6 4H11v5H7v-5H4v-4h3v-2a6 6 0 0 1 6-6h2Z" />
      </svg>
    ),
    instagram: (
      <svg {...commonProps}>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <path d="M17.5 6.5h.01" />
      </svg>
    ),
    whatsapp: (
      <svg {...commonProps}>
        <path d="M20 11.5a8.5 8.5 0 0 1-12.57 7.5L4 20l1.1-3.23A8.5 8.5 0 1 1 20 11.5Z" />
        <path d="M8.8 9.4c.2-.5.4-.5.7-.5h.6c.2 0 .4 0 .6.5.2.5.8 1.8.9 1.9.1.2.1.4 0 .6-.1.2-.2.4-.4.6l-.3.3c-.2.2-.4.4-.2.7.2.4.9 1.5 2 2 .5.3.8.2 1.1 0l.5-.6c.2-.2.4-.2.7-.1.3.1 1.9.9 2.2 1 .4.2.6.2.7.4.1.2.1 1-.2 1.4-.3.4-1.8 1-2.5 1-1.6 0-4.5-1.8-6.2-4.7-.8-1.3-1.2-2.5-.6-3.7Z" />
      </svg>
    ),
    youtube: (
      <svg {...commonProps}>
        <path d="M22 12s0-3-1-4.5c-.7-1.1-1.6-1.2-2.1-1.3C15.9 6 12 6 12 6h0s-3.9 0-6.9.2c-.5.1-1.4.2-2.1 1.3C2 9 2 12 2 12s0 3 1 4.5c.7 1.1 1.7 1.2 2.1 1.3 3 .2 6.9.2 6.9.2s3.9 0 6.9-.2c.5-.1 1.4-.2 2.1-1.3 1-1.5 1-4.5 1-4.5Z" />
        <path d="m10 9 5 3-5 3Z" />
      </svg>
    ),
    gmail: (
      <svg {...commonProps}>
        <path d="M4 6h16v12H4Z" />
        <path d="m4 7 8 6 8-6" />
      </svg>
    ),
    call: (
      <svg {...commonProps}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72l.36 2.57a2 2 0 0 1-.57 1.72l-1.84 1.84a16 16 0 0 0 6 6l1.84-1.84a2 2 0 0 1 1.72-.57l2.57.36A2 2 0 0 1 22 16.92Z" />
      </svg>
    )
  };

  return icons[type] || null;
}

const aboutStats = [
  {
    value: "10+",
    title: "Years of Proven Results",
    description: "Trusted coaching guidance with steady academic outcomes across Commerce and Science batches.",
    featured: true
  },
  {
    value: "99+",
    title: "Percentile Faculties",
    description: "Mentors focused on concept clarity, board readiness, and sharper performance."
  },
  {
    value: "30",
    title: "Max Batch Size",
    description: "Personalized attention, better interaction, and closer academic tracking."
  }
];

const aboutAdvantages = [
  {
    icon: "BK",
    title: "Study Material",
    description: "Complete notes, chapter-wise practice, revision sheets, and board-focused preparation support."
  },
  {
    icon: "TS",
    title: "Tests And Analysis",
    description: "Regular unit tests, paper discussion, and progress tracking to improve scoring confidence."
  }
];

const aboutSections = [
  {
    key: "about",
    menu: "About Coaching",
    title: "About Coaching",
    introTitle: "About Bright Line Coaching Classes",
    intro:
      '“Take a Trial. See the Difference.” Welcome to Bright Line Classes, Kadodara (Surat) — your trusted coaching destination for Class 6 to 12 students in Commerce and Science. We specialize in CBSE and GSEB curriculum with a strong focus on concept clarity, disciplined learning, and consistent academic improvement. Our experienced faculty provides personal attention, regular tests, and dedicated doubt-solving sessions so every student can perform at their best.',
    bullets: [
      "Founded on 10 June 2016, Bright Line Coaching Classes has built a trusted name in quality education and result-driven guidance.",
      "We believe every student has the potential to succeed with the right academic support, strong fundamentals, and confidence-building mentorship.",
      "At Bright Line Classes, we do not just prepare students for exams — we help them grow into stronger, more confident learners for the future."
    ],
    sectionTitle: "Why Choose Bright Line Coaching Classes?",
    sectionIntro:
      "Bright Line Coaching Classes is built around discipline, conceptual clarity, and steady academic growth. These are the pillars that make our institute a trusted learning environment for school students.",
    sectionBullets: [
      "Strong Concept Clarity",
      "Experienced and Supportive Faculty",
      "Personal Attention to Every Student",
      "Regular Tests and Performance Tracking",
      "Daily Doubt Solving Sessions",
      "Simple and Easy Teaching Methods",
      "Exam-Oriented Preparation",
      "Friendly and Motivating Environment"
    ],
    readMore: "Join Bright Line Classes and experience the difference in learning.",
    extraSections: [
      {
        title: "Our Mission",
        content:
          "To provide quality education, build confidence, and empower students to achieve academic excellence through dedication, discipline, and proper guidance."
      },
      {
        title: "Our Vision",
        content:
          "To become a leading educational institute known for excellence, trust, and student success."
      },
      {
        title: "What Makes Us Different?",
        content:
          "We focus on understanding, not just memorizing. With small batches, consistent progress monitoring, and dedicated support for every student, Bright Line Classes creates a learning environment where students improve steadily and confidently."
      }
    ]
  },
  {
    key: "competencies",
    menu: "Coaching Competencies",
    title: "Coaching Competencies",
    introTitle: "Our Teaching Competencies",
    intro:
      "At Bright Line Classes, our strength lies in the quality of teaching and the systems we follow every day. Our teaching competencies are designed to improve concept clarity, academic confidence, and exam performance for Class 6 to 12 students across Commerce and Science.",
    bullets: [
      "Experienced and dedicated faculty with strong subject command.",
      "Concept-based teaching methods that simplify difficult topics.",
      "Regular testing and assessment to measure progress.",
      "Performance analysis and feedback for continuous improvement.",
      "Personal guidance and daily doubt-solving support."
    ],
    sectionTitle: "How We Build Student Success",
    sectionIntro:
      "Our classrooms are designed to support disciplined study, regular revision, and steady academic growth so that every student gets the right balance of support and challenge.",
    sectionBullets: [
      "Structured study material for effective practice and revision.",
      "Exam-oriented preparation aligned with CBSE and GSEB patterns.",
      "Personal attention through small-batch focused teaching.",
      "Confidence-building support through continuous encouragement and monitoring."
    ],
    readMore: "Our approach is simple: teach clearly, guide consistently, and help every student move toward better results."
  },
  {
    key: "commerce",
    menu: "Commerce Coaching",
    title: "Commerce Coaching",
    introTitle: "Focused support for Commerce students",
    intro:
      "Commerce coaching at Bright Line Classes, Kadodara (Surat) is designed for Class 6 to 12 students with stronger support in Accounts, Statistics, Economics, and Business Studies. We follow CBSE and GSEB standards while keeping teaching practical, simple, and exam-focused.",
    bullets: [
      "Strong concept building in core Commerce subjects.",
      "Personal attention with regular doubt solving and practice.",
      "Regular tests, performance tracking, and revision planning."
    ],
    sectionTitle: "What Students Receive In Commerce Coaching",
    sectionIntro:
      "Our Commerce batches are built to strengthen fundamentals first and then improve scoring confidence through regular guidance and exam-oriented preparation.",
    sectionBullets: [
      "Chapter-wise teaching with easy-to-understand explanations.",
      "Exam-oriented preparation for school and board performance.",
      "Revision sessions to improve retention and confidence.",
      "Dedicated support for better answer-writing and consistent performance."
    ],
    readMore: "Bright Line Commerce coaching focuses on better understanding, better marks, and a brighter academic future."
  },
  {
    key: "science",
    menu: "Science Coaching",
    title: "Science Coaching",
    introTitle: "Concept-based preparation for Science students",
    intro:
      "Science coaching is built around concept understanding, doubt solving, and consistent academic routine. Students are supported with structured class follow-up so they improve steadily in core subjects and remain prepared for school examinations and long-term academic confidence.",
    bullets: [
      "Concept-first classroom teaching with doubt-clearing support.",
      "Routine academic follow-up and revision-based improvement.",
      "Balanced guidance for stronger understanding and consistency."
    ],
    readMore: "Read more about Science Coaching"
  },
  {
    key: "gseb",
    menu: "GSEB",
    title: "GSEB Preparation",
    introTitle: "Structured support for Gujarat board students",
    intro:
      "Bright Line Classes supports GSEB students with practical syllabus coverage, chapter-wise tests, and disciplined revision strategy. The coaching environment helps students stay focused on textbook concepts, school exam requirements, and year-round preparation goals.",
    bullets: [
      "Board-oriented preparation with chapter-wise testing.",
      "Stronger revision support aligned with school requirements.",
      "Academic discipline for more consistent performance."
    ],
    readMore: "Read more about GSEB Coaching"
  },
  {
    key: "cbse",
    menu: "CBSE",
    title: "CBSE Preparation",
    introTitle: "Academic support for CBSE school students",
    intro:
      "CBSE-focused coaching is planned around concept understanding, written practice, and revision support for school performance. Bright Line Classes provides a more disciplined learning setup so students can build clarity, stay organized, and manage their academic routine with confidence.",
    bullets: [
      "Concept clarity with revision and practice-based support.",
      "Classroom routine designed for continuity and follow-up.",
      "Clear academic guidance for school-level CBSE preparation."
    ],
    readMore: "Read more about CBSE Coaching"
  },
  {
    key: "deeper-dive",
    menu: "Deeper Dive",
    title: "About Coaching - A Deeper Dive",
    introTitle: "Frequently explored coaching topics",
    intro:
      "For additional information, we have compiled summaries and specific resources in response to our affiliate's most frequently searched topics, as noted below. Let us know what additional resources and information you'd like to see.",
    bullets: [
      "Coaching, counseling and mentoring",
      "Coaching and psychotherapy",
      "Types of coaching",
      "Selecting a coach",
      "Internal versus external coaching",
      "When to use an internal coach",
      "Challenges and opportunities with internal coaching",
      "The coaching relationship",
      "Coaching models",
      "Ethics in coaching",
      "Diversity in coaching",
      "Boundaries and barriers in coaching"
    ],
    readMore: "Read more about core coaching concepts"
  }
];

function PublicSite({ currentPage, onNavigate, onLoginClick }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const extractMonthDay = (dobValue) => {
    const value = String(dobValue || "").trim();
    if (!value) {
      return null;
    }

    const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      return { month: ymd[2], day: ymd[3] };
    }

    const dmy = value.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (dmy) {
      return { month: dmy[2], day: dmy[1] };
    }

    return null;
  };
  const [activeAboutSection, setActiveAboutSection] = useState("about");
  const [facultyMembers, setFacultyMembers] = useState([]);
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [aboutLoading, setAboutLoading] = useState(false);
  const [shiningStars, setShiningStars] = useState([]);
  const [campusActivities, setCampusActivities] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [aboutCarouselSlides, setAboutCarouselSlides] = useState([]);
  const [currentTopperIndex, setCurrentTopperIndex] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    mobile: "",
    details: ""
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState("");
  const contactFormRef = useRef(null);
  const topperTouchStartX = useRef(0);
  const topperTouchDeltaX = useRef(0);
  const activeAboutContent =
    aboutSections.find((section) => section.key === activeAboutSection) || aboutSections[0];
  useEffect(() => {
    if (currentPage !== "faculty") {
      return;
    }

    const loadFaculty = async () => {
      try {
        setFacultyLoading(true);
        const response = await getTeachers("all", { includeImages: true });
        setFacultyMembers(response.data);
      } catch (error) {
        console.error("Failed to load faculty:", error);
        setFacultyMembers([]);
      } finally {
        setFacultyLoading(false);
      }
    };

    loadFaculty();
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== "gallery") {
      return;
    }

    const loadGallery = async () => {
      try {
        setGalleryLoading(true);
        const [starsRes, activitiesRes] = await Promise.all([
          getShiningStars(),
          getCampusActivities()
        ]);
        const today = new Date();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        const birthdayRes = await getStudentsByDob({ month, day, includeImages: true });
        const birthdayStudents = (birthdayRes.data || []).filter((student) => {
          const md = extractMonthDay(student.dob);
          return md && md.month === month && md.day === day;
        });
        setShiningStars(starsRes.data);
        setCampusActivities(activitiesRes.data);
        setTodayBirthdays(birthdayStudents);
      } catch (error) {
        console.error("Failed to load gallery content:", error);
        setShiningStars([]);
        setCampusActivities([]);
        setTodayBirthdays([]);
      } finally {
        setGalleryLoading(false);
      }
    };

    loadGallery();

    const intervalId = window.setInterval(loadGallery, 30000);
    const handleFocus = () => loadGallery();
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== "about") {
      return;
    }

    const loadAboutCarousel = async () => {
      try {
        setAboutLoading(true);
        const response = await getAboutCarouselSlides();
        setAboutCarouselSlides(response.data);
      } catch (error) {
        console.error("Failed to load about carousel:", error);
        setAboutCarouselSlides([]);
      } finally {
        setAboutLoading(false);
      }
    };

    loadAboutCarousel();
  }, [currentPage]);

  useEffect(() => {
    if (aboutCarouselSlides.length === 0) {
      setCurrentTopperIndex(0);
      return;
    }

    setCurrentTopperIndex((current) => (current >= aboutCarouselSlides.length ? 0 : current));
  }, [aboutCarouselSlides]);

  useEffect(() => {
    if (currentPage !== "about" || aboutCarouselSlides.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCurrentTopperIndex((current) => (current + 1) % aboutCarouselSlides.length);
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [currentPage, aboutCarouselSlides]);

  useEffect(() => {
    if (currentPage === "contact" && window.location.hash === "#demo-form") {
      window.setTimeout(() => {
        contactFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [currentPage]);

  const handleContactChange = (field, value) => {
    setContactForm((current) => ({
      ...current,
      [field]: value
    }));
    if (contactSubmitted) {
      setContactSubmitted(false);
    }
    if (contactError) {
      setContactError("");
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactSubmitting(true);
    setContactError("");

    try {
      await createInquiry({
        name: contactForm.name,
        email: contactForm.email,
        mobile: contactForm.mobile,
        details: contactForm.details
      });
      setContactSubmitted(true);
      setContactForm({
        name: "",
        email: "",
        mobile: "",
        details: ""
      });
    } catch (error) {
      setContactSubmitted(false);
      setContactError(
        error.response?.data?.error || "We could not submit your inquiry right now. Please try again."
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const featuredTopper = aboutCarouselSlides[currentTopperIndex];
  const hasMultipleTopperSlides = aboutCarouselSlides.length > 1;
  const currentTopperGroupStart =
    Math.floor(currentTopperIndex / ABOUT_CAROUSEL_VISIBLE_COUNT) * ABOUT_CAROUSEL_VISIBLE_COUNT;
  const visibleTopperSlides = aboutCarouselSlides
    .slice(currentTopperGroupStart, currentTopperGroupStart + ABOUT_CAROUSEL_VISIBLE_COUNT)
    .map((slide, index) => ({
      slide,
      index: currentTopperGroupStart + index
    }));

  const handleTopperTouchStart = (event) => {
    if (!hasMultipleTopperSlides) {
      return;
    }
    topperTouchStartX.current = event.touches[0]?.clientX || 0;
    topperTouchDeltaX.current = 0;
  };

  const handleTopperTouchMove = (event) => {
    if (!hasMultipleTopperSlides) {
      return;
    }
    const currentX = event.touches[0]?.clientX || 0;
    topperTouchDeltaX.current = currentX - topperTouchStartX.current;
  };

  const handleTopperTouchEnd = () => {
    if (!hasMultipleTopperSlides) {
      return;
    }
    const swipeThreshold = 45;
    const deltaX = topperTouchDeltaX.current;
    if (Math.abs(deltaX) < swipeThreshold) {
      topperTouchDeltaX.current = 0;
      return;
    }
    if (deltaX < 0) {
      setCurrentTopperIndex((current) => (current + 1) % aboutCarouselSlides.length);
    } else {
      setCurrentTopperIndex((current) => (current - 1 + aboutCarouselSlides.length) % aboutCarouselSlides.length);
    }
    topperTouchDeltaX.current = 0;
  };

  const renderPublicLoader = (label) => (
    <div className="public-site__inline-loader" aria-label={label}>
      <div className="public-site__inline-loader-box">
        <img src="/rita-tech-logo.png" alt="Loading" className="public-site__inline-loader-image" />
      </div>
    </div>
  );

  
  return (
    <div className="public-site">
      <div className="public-site__topbar">
        <div className="public-site__topbar-contact">
          <a href="tel:+919724700280">Call Us: +91 97247 00280</a>
          <a href="mailto:brightlineclasses2018@gmail.com">brightlineclasses2018@gmail.com</a>
        </div>
        <div className="public-site__topbar-socials">
          <a href="https://www.facebook.com/kunalr284" target="_blank" rel="noreferrer" aria-label="Facebook">
            <SocialIcon type="facebook" />
          </a>
          <a href="https://www.instagram.com/classesbrightline/" target="_blank" rel="noreferrer" aria-label="Instagram">
            <SocialIcon type="instagram" />
          </a>
          <a href="https://youtube.com/@brightlineclasses5462?si=izp8dWBqFNwFEQVN" target="_blank" rel="noreferrer" aria-label="YouTube">
            <SocialIcon type="youtube" />
          </a>
          <a href="mailto:brightlineclasses2018@gmail.com" aria-label="Gmail">
            <SocialIcon type="gmail" />
          </a>
          <a href="https://wa.me/919724700280" target="_blank" rel="noreferrer" aria-label="WhatsApp">
            <SocialIcon type="whatsapp" />
          </a>
        </div>
      </div>

      <header className="public-site__header">
        <div className="public-site__brand">
          <img src="/logo.jpeg" alt="Bright Line Classes" className="public-site__logo" />
          <div>
            <strong>Bright Line Classes</strong>
            <span>Commerce and Science Coaching</span>
          </div>
        </div>

        <button
          className="public-site__menu-toggle"
          onClick={() => setMobileNavOpen((current) => !current)}
          aria-label="Toggle menu"
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`public-site__nav ${mobileNavOpen ? "is-open" : ""}`}>
          <button
            className={`public-site__nav-link ${currentPage === "about" ? "active" : ""}`}
            onClick={() => {
              onNavigate("about");
              setMobileNavOpen(false);
            }}
          >
            About Us
          </button>
          <button
            className={`public-site__nav-link ${currentPage === "faculty" ? "active" : ""}`}
            onClick={() => {
              onNavigate("faculty");
              setMobileNavOpen(false);
            }}
          >
            Faculty
          </button>
          <button
            className={`public-site__nav-link ${currentPage === "gallery" ? "active" : ""}`}
            onClick={() => {
              onNavigate("gallery");
              setMobileNavOpen(false);
            }}
          >
            Gallery
          </button>
          <button
            className={`public-site__nav-link ${currentPage === "contact" ? "active" : ""}`}
            onClick={() => {
              onNavigate("contact");
              setMobileNavOpen(false);
            }}
          >
            Contact Us
          </button>
          {mobileNavOpen && (
            <>
              <button
                className="public-site__nav-link public-site__nav-link--cta"
                onClick={() => {
                  window.location.hash = "demo-form";
                  onNavigate("contact");
                  setMobileNavOpen(false);
                }}
              >
                Book Free Demo
              </button>
              <button
                className="public-site__nav-link public-site__nav-link--cta"
                onClick={() => {
                  onLoginClick();
                  setMobileNavOpen(false);
                }}
              >
                Login
              </button>
            </>
          )}
        </nav>

        <button
          className="public-site__demo-btn"
          onClick={() => {
            window.location.hash = "demo-form";
            onNavigate("contact");
          }}
        >
          Book Free Demo
        </button>

        <button className="public-site__login-btn" onClick={onLoginClick}>
          Login
        </button>
      </header>

      <div className="public-site__admission-ticker" aria-label="Admission announcement">
        <div className="public-site__admission-ticker-track">
          <span>Limited Seats Available – Take Admission Now! 📞 97247 00280</span>
          <span>Admissions Open Now! Call 97247 00280 for Best Coaching Classes</span>
          <span>Quality Education, Guaranteed Results – Call 97247 00280</span>
        </div>
      </div>

      <main className="public-site__content">
        {currentPage === "about" && (
          <section className="public-site__page-section public-site__about-section">
            {aboutLoading && (
              <div className="public-site__page-loader-overlay">
                {renderPublicLoader("Loading about carousel")}
              </div>
            )}
            {featuredTopper && (
              <>
                <section className="public-site__about-topper-carousel">
                  <div className="public-site__about-topper-media">
                    <div
                      className="public-site__about-topper-stage"
                      style={{ "--topper-index": currentTopperIndex }}
                      onTouchStart={handleTopperTouchStart}
                      onTouchMove={handleTopperTouchMove}
                      onTouchEnd={handleTopperTouchEnd}
                      onTouchCancel={handleTopperTouchEnd}
                    >
                      {visibleTopperSlides.map(({ slide, index }) => (
                        <button
                          key={`${slide.title}-${slide._id || index}`}
                          type="button"
                          className={`public-site__about-topper-frame ${index === currentTopperIndex ? "active" : ""}`}
                          onClick={() => setCurrentTopperIndex(index)}
                          aria-label={`Highlight ${slide.title}`}
                        >
                          <img
                            src={slide.image}
                            alt={slide.title}
                            className="public-site__about-topper-image"
                            loading={index === currentTopperIndex ? "eager" : "lazy"}
                            fetchPriority={index === currentTopperIndex ? "high" : "low"}
                            decoding="async"
                          />
                          <span className="public-site__about-topper-frame-name">{slide.title}</span>
                        </button>
                      ))}
                    </div>
                    <div className="public-site__about-topper-badge">
                      <span>{featuredTopper.badge || "Bright Line Classes"}</span>
                      <strong>{String(currentTopperIndex + 1).padStart(2, "0")}</strong>
                    </div>
                  </div>

                  <div className="public-site__about-topper-copy">
                    <span className="public-site__eyebrow">Shining Stars</span>
                    <h2>{featuredTopper.title}</h2>
                    <h3>{featuredTopper.subtitle}</h3>
                    <p>{featuredTopper.description}</p>

                    {hasMultipleTopperSlides && (
                      <div className="public-site__about-topper-dots" aria-label="Topper carousel navigation">
                        {aboutCarouselSlides.map((slide, index) => (
                          <button
                            key={`${slide.title}-${slide._id || index}`}
                            type="button"
                            className={`public-site__about-topper-dot ${index === currentTopperIndex ? "active" : ""}`}
                            onClick={() => setCurrentTopperIndex(index)}
                            aria-label={`Show slide ${slide.title}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            <div className="public-site__about-detail-layout">
              <aside className="public-site__about-sidebar">
                {aboutSections.map((section) => (
                  <button
                    key={section.key}
                    className={`public-site__about-nav-item ${activeAboutSection === section.key ? "active" : ""}`}
                    onClick={() => setActiveAboutSection(section.key)}
                  >
                    {section.menu}
                  </button>
                ))}
              </aside>

              <article className="public-site__about-article">
                <span className="public-site__eyebrow">Bright Line Classes</span>
                <h2>{activeAboutContent.title}</h2>
                <h3>{activeAboutContent.introTitle}</h3>
                <p>{activeAboutContent.intro}</p>
                <ul className="public-site__about-bullets">
                  {activeAboutContent.bullets.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                {activeAboutContent.sectionTitle && (
                  <>
                    <h3>{activeAboutContent.sectionTitle}</h3>
                    <p>{activeAboutContent.sectionIntro}</p>
                    <ul className="public-site__about-bullets">
                      {activeAboutContent.sectionBullets.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </>
                )}

                {activeAboutContent.readMore && (
                  <a className="public-site__about-readmore" href="#top">
                    {activeAboutContent.readMore}
                  </a>
                )}

                {activeAboutContent.extraSections?.map((section) => (
                  <div key={section.title} className="public-site__about-extra-block">
                    <h3>{section.title}</h3>
                    <p>{section.content}</p>
                  </div>
                ))}
              </article>
            </div>

            <div className="public-site__about-grid">
              <div className="public-site__about-stats">
                {aboutStats.map((item) => (
                  <article
                    key={item.title}
                    className={`public-site__about-stat-card ${item.featured ? "featured" : ""}`}
                  >
                    <strong>{item.value}</strong>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                ))}
              </div>

              <article className="public-site__about-copy">
                <span className="public-site__faculty-eyebrow">Why Choose Us</span>
                <h1>
                  The Bright Line <span>Advantage</span>
                </h1>
                <p>
                  We do not just teach. We build confidence, strategy, discipline, and year-round
                  academic support so students can perform better in school and board examinations.
                </p>

                <div className="public-site__about-feature-list">
                  {aboutAdvantages.map((item) => (
                    <div key={item.title} className="public-site__about-feature-item">
                      <div className="public-site__about-feature-icon">{item.icon}</div>
                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </section>
        )}

        {currentPage === "gallery" && (
          <section className="public-site__page-section">
            {galleryLoading && (
              <div className="public-site__page-loader-overlay">
                {renderPublicLoader("Loading gallery content")}
              </div>
            )}
            {/* <div className="public-site__section-head">
              <span className="public-site__eyebrow">Gallery</span>
              <h1>Our Shining Stars</h1>
              <p>A visual showcase of Bright Line Classes, classroom highlights, result moments, and institute identity.</p>
            </div> */}

            <section className="public-site__topper-section">
              <div className="public-site__section-head">
                <div>
                  <span className="public-site__eyebrow">Shining Stars</span>
                  <h2>Bright Line Highlights</h2>
                </div>
                {/* <p>All institute photos collected into one poster-style showcase.</p> */}
              </div>

              <div className={`public-site__module-block ${galleryLoading ? "is-loading" : ""}`}>
                <div className="public-site__topper-grid">
                {shiningStars.map((student) => (
                  <article key={`${student.name}-${student.posterIndex}`} className="public-site__topper-card">
                    <div className="public-site__topper-poster">
                      <div className="public-site__topper-brand-mark">
                        <img src="/logo.jpeg" alt="Bright Line Classes" />
                      </div>
                      <div className="public-site__topper-photo-frame">
                        <img
                          src={student.image}
                          alt={student.name}
                          className="public-site__topper-image"
                          loading="lazy"
                          decoding="async"
                          fetchPriority="low"
                        />
                      </div>
                      <div className="public-site__topper-ribbon">Congratulations</div>
                      <div className="public-site__topper-nameplate">{student.name}</div>
                      <div className="public-site__topper-achievement">
                        <strong>{student.resultLabel}</strong>
                        <span>{student.stream}</span>
                      </div>
                      <div className="public-site__topper-score-badge">{student.posterIndex}</div>
                    </div>
                    <div className="public-site__topper-copy">
                      <p>{student.highlight}</p>
                    </div>
                  </article>
                ))}
                {!galleryLoading && shiningStars.length === 0 && (
                  <div className="public-site__faculty-empty">No shining stars are available right now.</div>
                )}
                </div>
              </div>
            </section>

            {todayBirthdays.length > 0 && (
              <section className="public-site__birthday-section">
                <div className="public-site__photo-gallery-head">
                  <span className="public-site__eyebrow">Today Birthdays</span>
                  <h2>Birthday Wishes</h2>
                </div>
                <div className="public-site__birthday-grid">
                  {todayBirthdays.map((student) => (
                    <article key={student._id} className="public-site__birthday-card">
                      <div className="public-site__birthday-headline">
                        <span>HAPPY</span>
                        <span>BIRTHDAY</span>
                      </div>
                      <div className="public-site__birthday-poster">
                        <div className="public-site__birthday-photo-wrap">
                          <img
                            src={student.imageData || student.imageUrl || "/logo.jpeg"}
                            alt={student.name}
                            className="public-site__birthday-photo"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div className="public-site__birthday-name">{student.name}</div>
                        <div className="public-site__birthday-status">
                          Many Many Happy Return Of The Day
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <section className="public-site__photo-gallery-section">
              <div className="public-site__photo-gallery-head">
                <span className="public-site__eyebrow">Photo Gallery</span>
                <h2>Campus Moments And Academic Activities</h2>
              </div>

              <div className={`public-site__module-block ${galleryLoading ? "is-loading" : ""}`}>
                <div className="public-site__photo-gallery-grid">
                {campusActivities.map((item) => (
                  <article key={`${item.title}-${item.image}`} className="public-site__photo-gallery-card">
                    <div className="public-site__photo-gallery-media">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="public-site__photo-gallery-image"
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                      />
                    </div>
                    <div className="public-site__photo-gallery-caption">{item.title}</div>
                  </article>
                ))}
                {!galleryLoading && campusActivities.length === 0 && (
                  <div className="public-site__faculty-empty">No campus activities are available right now.</div>
                )}
                </div>
              </div>
            </section>
          </section>
        )}

        {currentPage === "faculty" && (
          <section className="public-site__faculty-page">
            <div className="public-site__faculty-hero">
              <span className="public-site__faculty-eyebrow">Expert Educators</span>
              <h1>
                Learn from the <span>Best.</span>
              </h1>
              <p>
                Our faculty guide students with subject expertise, classroom discipline, and a stronger
                academic support system across Commerce and Science.
              </p>
            </div>

            <div className={`public-site__module-block ${facultyLoading ? "is-loading" : ""}`}>
              {facultyLoading && (
                <div className="public-site__module-loader-overlay">
                  {renderPublicLoader("Loading faculty members")}
                </div>
              )}
            {facultyMembers.length === 0 && !facultyLoading ? (
              <div className="public-site__faculty-empty">No faculty records are available right now.</div>
            ) : (
              <div className="public-site__faculty-grid">
                {facultyMembers.map((teacher) => {
                  const imageSource = teacher.imageData || teacher.imageUrl || "/logo.jpeg";
                  return (
                    <article key={teacher._id} className="public-site__faculty-card">
                      <div className="public-site__faculty-photo-wrap">
                        <img
                          src={imageSource}
                          alt={teacher.name}
                          className="public-site__faculty-photo"
                        />
                      </div>
                      <div className="public-site__faculty-copy">
                        <h3>{teacher.name}</h3>
                        <span>{teacher.subject || "Faculty Member"}</span>
                        <p>{teacher.experience || "Experienced educator"}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
            </div>
          </section>
        )}

        {currentPage === "contact" && (
          <section className="public-site__page-section">
            <div className="public-site__contact-shell">
              <section className="public-site__contact-sidepanel">
                <span className="public-site__contact-side-eyebrow">Let's get in touch</span>
                <h2>We're here to guide your next academic step.</h2>
                <p>
                  Reach out for admissions, batch timing details, free demo booking, or direct
                  counseling support for Commerce and Science programs.
                </p>

                <div className="public-site__contact-side-list">
                  <div className="public-site__contact-side-item">
                    <div className="public-site__contact-side-icon public-site__contact-info-icon--address">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 21s6-4.35 6-10a6 6 0 1 0-12 0c0 5.65 6 10 6 10Z" />
                        <circle cx="12" cy="11" r="2.5" />
                      </svg>
                    </div>
                    <div>
                      <strong>Address</strong>
                      <span>208, 209, 220 Shyam Arcade, Near Chamunda Chai, Kadodara Char Rasta</span>
                    </div>
                  </div>
                  <div className="public-site__contact-side-item">
                    <div className="public-site__contact-side-icon public-site__contact-info-icon--phone">
                      <SocialIcon type="call" />
                    </div>
                    <div>
                      <strong>Phone</strong>
                      <span>9724700280 / 91665 62654</span>
                    </div>
                  </div>
                  <div className="public-site__contact-side-item">
                    <div className="public-site__contact-side-icon public-site__contact-info-icon--email">
                      <SocialIcon type="gmail" />
                    </div>
                    <div>
                      <strong>Email</strong>
                      <span>brightlineclasses2018@gmail.com</span>
                    </div>
                  </div>
                  <div className="public-site__contact-side-item">
                    <div className="public-site__contact-side-icon public-site__contact-info-icon--hours">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="8.5" />
                        <path d="M12 7.5V12l3 2" />
                      </svg>
                    </div>
                    <div>
                      <strong>Office Hours</strong>
                      <span>Mon-Fri 3-7 pm | Sat 3-7 pm | Sun 10 am-1 pm</span>
                    </div>
                  </div>
                </div>
              </section>

              <section
                ref={contactFormRef}
                id="demo-form"
                className="public-site__contact-maincard"
              >
                <div className="public-site__contact-mainhead">
                  <h2>Get in touch</h2>
                  <p>Your inquiry goes directly to our team and is saved for follow-up.</p>
                </div>

                <form className="public-site__contact-form public-site__contact-form--template" onSubmit={handleContactSubmit}>
                  <div className="public-site__contact-form-row">
                    <label>
                      Full Name
                      <input
                        type="text"
                        value={contactForm.name}
                        onChange={(event) => handleContactChange("name", event.target.value)}
                        placeholder="Name"
                        required
                      />
                    </label>
                    <label>
                      Email Address
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(event) => handleContactChange("email", event.target.value)}
                        placeholder="Email"
                        required
                      />
                    </label>
                  </div>
                  <label>
                    Mobile Number
                    <input
                      type="tel"
                      value={contactForm.mobile}
                      onChange={(event) => handleContactChange("mobile", event.target.value)}
                      placeholder="Mobile number"
                      required
                    />
                  </label>
                  <label>
                    Message
                    <textarea
                      value={contactForm.details}
                      onChange={(event) => handleContactChange("details", event.target.value)}
                      placeholder="Write your message"
                      rows="6"
                      required
                    />
                  </label>
                  <div className="public-site__contact-form-actions">
                    <button className="public-site__contact-submit" type="submit" disabled={contactSubmitting}>
                      {contactSubmitting ? "Submitting..." : "Send Message"}
                    </button>
                    <a
                      className="public-site__contact-whatsapp-btn public-site__contact-whatsapp-btn--inline"
                      href="https://wa.me/919724700280"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <SocialIcon type="whatsapp" /> WhatsApp Us
                    </a>
                  </div>
                </form>

                {contactSubmitted && (
                  <p className="public-site__contact-success">
                    Thank you. Your details have been recorded and our team will connect with you soon.
                  </p>
                )}

                {contactError && <p className="public-site__contact-error">{contactError}</p>}

                <div className="public-site__map-frame public-site__map-frame--contact-template">
                  <iframe
                    title="Bright Line Classes Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.5362187747346!2d72.9646021!3d21.1708474!2m3!1f0!2f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be044cb9cd6fd0b%3A0x453aebee38f457c0!2sBright%20Line%20Classes!5e0!3m2!1sen!2sin!4v1777099648066!5m2!1sen!2sin"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </section>
            </div>
          </section>
        )}
      </main>

      <footer className="public-site__footer">
        <div className="public-site__footer-top">
          <div className="public-site__footer-column">
            <strong className="public-site__footer-title">Stay Connected</strong>
            <div className="public-site__footer-link-list">
              <a href="https://wa.me/919724700280" target="_blank" rel="noreferrer">Chat on WhatsApp</a>
              <a href="https://www.instagram.com/classesbrightline/" target="_blank" rel="noreferrer">See classroom updates</a>
              <a href="https://youtube.com/@brightlineclasses5462?si=izp8dWBqFNwFEQVN" target="_blank" rel="noreferrer">Watch Bright Line videos</a>
            </div>
          </div>

          <div className="public-site__footer-column">
            <strong className="public-site__footer-title">Admissions & Support</strong>
            <span>Connect with Bright Line Classes for Commerce and Science admissions, schedules, and inquiry support.</span>
            <div className="public-site__footer-cta-row">
              <button className="public-site__footer-ghost" onClick={() => onNavigate("contact")}>Contact Us</button>
            </div>
          </div>

          <div className="public-site__footer-column">
            <strong className="public-site__footer-title">Contact Us</strong>
            <div className="public-site__footer-address">
              <span>Bright Line Classes</span>
              <span>208, 209, 220 Shyam Arcade</span>
              <span>Near Chamunda Chai, Kadodara Char Rasta</span>
              <span>9724700280 / 91665 62654</span>
              <span>brightlineclasses2018@gmail.com</span>
            </div>
          </div>
        </div>

        <div className="public-site__footer-bottom">
          <div className="public-site__footer-copy">
            <strong>Copyright {new Date().getFullYear()} Bright Line Classes. All rights reserved.</strong>
            <span>Commerce and Science coaching with connected academic management.</span>
          </div>
          <div className="public-site__footer-social-wrap">
            <span className="public-site__footer-social-label">Connect with us:</span>
            <div className="public-site__footer-socials">
            <a href="https://www.facebook.com/kunalr284" target="_blank" rel="noreferrer" aria-label="Facebook">
              <SocialIcon type="facebook" />
            </a>
            <a href="https://www.instagram.com/classesbrightline/" target="_blank" rel="noreferrer" aria-label="Instagram">
              <SocialIcon type="instagram" />
            </a>
            <a href="https://youtube.com/@brightlineclasses5462?si=izp8dWBqFNwFEQVN" target="_blank" rel="noreferrer" aria-label="YouTube">
              <SocialIcon type="youtube" />
            </a>
            <a href="mailto:brightlineclasses2018@gmail.com" aria-label="Gmail">
              <SocialIcon type="gmail" />
            </a>
            <a href="https://wa.me/919724700280" target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <SocialIcon type="whatsapp" />
            </a>
            </div>
          </div>
        </div>
      </footer>

      <div className="public-site__floating-actions" aria-label="Quick contact actions">
        <a
          className="public-site__floating-action public-site__floating-action--call"
          href="tel:+919724700280"
          aria-label="Call Bright Line Classes"
          title="Call Us"
        >
          <SocialIcon type="call" />
        </a>
        <a
          className="public-site__floating-action public-site__floating-action--gmail"
          href="mailto:brightlineclasses2018@gmail.com"
          aria-label="Email Bright Line Classes"
          title="Email Us"
        >
          <SocialIcon type="gmail" />
        </a>
        <a
          className="public-site__floating-action public-site__floating-action--whatsapp"
          href="https://wa.me/919724700280"
          target="_blank"
          rel="noreferrer"
          aria-label="Chat on WhatsApp"
          title="WhatsApp Us"
        >
          <SocialIcon type="whatsapp" />
        </a>
      </div>
    </div>
  );
}

export default PublicSite;
